import type {
  LocationRewrite,
  LocationRewriteFunction,
} from "@tanstack/react-router";

import { BASE_UI_CONFIG } from "~/lib/ui-config";
import {
  type AppLocale,
  getActiveLocale,
  LANGUAGE_REGISTRY,
  registerLocaleUrlHistorySync,
  setUrlLocaleOverride,
} from "./language-lens";

const BASE_LOCALE = BASE_UI_CONFIG.lenses.language.locale;
// Object.keys widens the keys even though the registry is a closed,
// const-asserted record.
const URL_PREFIX_LOCALES = (
  Object.keys(LANGUAGE_REGISTRY) as AppLocale[]
).filter((locale) => locale !== BASE_LOCALE);

function getLeadingPathSegment(pathname: string): string | null {
  if (!pathname.startsWith("/")) {
    return null;
  }

  const segmentEnd = pathname.indexOf("/", 1);
  const segment = pathname.slice(1, segmentEnd === -1 ? undefined : segmentEnd);
  return segment.length > 0 ? segment : null;
}

export function matchUrlLocalePrefix(pathname: string): AppLocale | null {
  const leadingSegment = getLeadingPathSegment(pathname)?.toLowerCase();
  if (leadingSegment === undefined) {
    return null;
  }

  return (
    URL_PREFIX_LOCALES.find(
      (locale) => locale.toLowerCase() === leadingSegment,
    ) ?? null
  );
}

export const rewriteLocaleUrlInput: LocationRewriteFunction = ({ url }) => {
  const locale = matchUrlLocalePrefix(url.pathname);
  setUrlLocaleOverride(locale);

  if (locale === null) {
    return undefined;
  }

  const segmentEnd = url.pathname.indexOf("/", 1);
  const rewrittenUrl = new URL(url);
  rewrittenUrl.pathname =
    segmentEnd === -1 ? "/" : url.pathname.slice(segmentEnd);
  return rewrittenUrl;
};

export const rewriteLocaleUrlOutput: LocationRewriteFunction = ({ url }) => {
  const locale = getActiveLocale();
  if (locale === BASE_LOCALE) {
    return undefined;
  }

  const rewrittenUrl = new URL(url);
  rewrittenUrl.pathname =
    url.pathname === "/" ? `/${locale}` : `/${locale}${url.pathname}`;
  return rewrittenUrl;
};

/**
 * Localizes a root-relative href for raw anchors that cannot use Router Link.
 * External, protocol-relative, hash-only, and mailto hrefs pass through.
 */
export function localizeHref(path: string): string {
  if (!path.startsWith("/") || path.startsWith("//")) {
    return path;
  }

  const baseUrl = new URL("https://locale-href.glider.invalid");
  const url = new URL(path, baseUrl);
  const rewritten = rewriteLocaleUrlOutput({ url });
  const localizedUrl =
    typeof rewritten === "string"
      ? new URL(rewritten, baseUrl)
      : (rewritten ?? url);

  return `${localizedUrl.pathname}${localizedUrl.search}${localizedUrl.hash}`;
}

export const localeUrlRewrite = {
  input: rewriteLocaleUrlInput,
  output: rewriteLocaleUrlOutput,
} satisfies LocationRewrite;

function stripUrlLocalePrefix(pathname: string): string {
  if (matchUrlLocalePrefix(pathname) === null) {
    return pathname;
  }
  const segmentEnd = pathname.indexOf("/", 1);
  return segmentEnd === -1 ? "/" : pathname.slice(segmentEnd);
}

/**
 * Rewrites the current browser history entry so its locale prefix matches the
 * active locale. Without this, an explicit picker choice made on a prefixed
 * URL (e.g. picking English on /de/...) leaves the stale prefix in history,
 * and the router's next load re-applies the old override (picker snap-back).
 */
export function syncHistoryEntryWithActiveLocale(): void {
  if (
    typeof window === "undefined" ||
    typeof window.history?.replaceState !== "function"
  ) {
    return;
  }

  const { hash, pathname, search } = window.location;
  const stripped = stripUrlLocalePrefix(pathname);
  const active = getActiveLocale();
  const next =
    active === BASE_LOCALE
      ? stripped
      : stripped === "/"
        ? `/${active}`
        : `/${active}${stripped}`;

  if (next !== pathname) {
    window.history.replaceState(
      window.history.state,
      "",
      `${next}${search}${hash}`,
    );
  }
}

registerLocaleUrlHistorySync(syncHistoryEntryWithActiveLocale);
